import React from 'react';
import styled from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { headerWatermarkStyles } from './dashboardWatermark';
import { MonumentCaption } from './MonumentCaption';
import { useMonumentWatermark } from '../../hooks/useMonumentWatermark';

const HeaderWrap = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 180px;
  display: flex;
  align-items: center;
  padding: ${spacing[8]};
  margin-bottom: ${spacing[6]};
  border-radius: ${borders.radius.lg};

  ${headerWatermarkStyles}

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[4]};
    min-height: auto;
  }
`;

const AccentBar = styled.span`
  display: block;
  width: 58px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.theme.colors.accent};
  margin-bottom: ${spacing[4]};
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 2;
  width: min(100%, 580px);
`;

const Greeting = styled.h1`
  font-family: ${typography.fontFamily.display};
  font-size: clamp(1.75rem, 2.5vw, 2.25rem);
  font-weight: 600;
  line-height: 1.2;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const Description = styled.p`
  margin: ${spacing[4]} 0 0 0;
  max-width: 36rem;
  line-height: ${typography.lineHeight.relaxed};
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.textSecondary};
`;

interface DashboardHeaderProps {
  greeting: string;
  description: string;
  /** Optional compact identity / meta row under the description. */
  meta?: React.ReactNode;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  greeting,
  description,
  meta,
}) => {
  const watermark = useMonumentWatermark();

  return (
    <HeaderWrap className="dashboard-welcome" style={watermark.style}>
      <HeaderContent className="dashboard-welcome__content">
        <AccentBar aria-hidden />
        <Greeting>{greeting}</Greeting>
        <Description>{description}</Description>
        {meta}
      </HeaderContent>
      <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
    </HeaderWrap>
  );
};

export default DashboardHeader;
